/**
 * Testers: everybody who has started a tester card on ridgewill.com
 * (RidgeWill/platform#56, auth `GET /admin/testers`, `tester:read`).
 *
 * ── A list you shape, then hand to a security group ─────────────────────
 *
 * Jon dropped the idea of one security group per application. Instead this
 * screen answers "who should get the next build", in three moves:
 *
 *   1. QUERY. Conditions over the card: the app they asked for, a device
 *      type or operating system, a minimum memory, the browser they use.
 *   2. ADJUST BY HAND. Take somebody out, or put somebody in the query did
 *      not find. Neither touches their card: it is this view, nothing more.
 *   3. ACT. Select all or some, then make a new security group of them or add
 *      them to an existing one. Access then flows the way it already does,
 *      from the group.
 *
 * The working list is (query results + added by hand) minus removed by hand,
 * and the strip above the table always says which of those it is showing,
 * so a list that was nudged never passes for a pure query.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type SecurityGroup, type TesterCardRow } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { count } from '../lib/format.js';
import { Menu, MenuItem, PrimaryButton, Screen, inputStyle } from '../components/Screen.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';

export type TesterDevice = {
  type: 'phone' | 'tablet' | 'computer';
  os: string;
  version: string;
  model: string;
  storage: string;
  memory: string;
  browser: string;
};
/** A card as this screen reads it: the server's row, flattened. */
export type Tester = {
  id: string;
  name: string;
  email: string;
  sent: boolean;
  submittedAt: string | null;
  updatedAt: string;
  apps: string[];
  devices: TesterDevice[];
  /** Answer KEYS (auth lib/tester-cards.ts); the words are below. */
  experience: string;
  feedback: string;
  time: string;
  accessibility: string[];
  inviteEmail: string;
};

function fromRow(r: TesterCardRow): Tester {
  return {
    id: r.userId,
    name: r.name,
    email: r.email,
    sent: r.submittedAt !== null,
    submittedAt: r.submittedAt,
    updatedAt: r.updatedAt,
    apps: r.apps,
    devices: r.devices,
    experience: r.answers.experience ?? '',
    feedback: r.answers.feedback ?? '',
    time: r.answers.time ?? '',
    accessibility: r.answers.accessibility ?? [],
    inviteEmail: r.answers.inviteEmail ?? '',
  };
}

/** The words for each stored answer, as ridgewill.com shows them. */
const EXPERIENCE: Record<string, string> = { regularly: 'Yes, regularly', some: 'A few times', never: 'Never, first time' };
const FEEDBACK: Record<string, string> = { detailed: 'Detailed, with screenshots', short: 'Short notes', unsure: 'Not sure yet' };
const TIME: Record<string, string> = { '15m': 'About 15 minutes a week', '1h': 'About an hour a week', more: 'A few hours a week' };
const ACCESSIBILITY: Record<string, string> = {
  'larger-text': 'Larger text',
  'screen-reader': 'VoiceOver or TalkBack',
  'reduced-motion': 'Reduced motion',
  'color-filters': 'Color filters',
};

/**
 * Application names, filled from the response when the screen loads. A module
 * value rather than props through six components: the screen is one instance,
 * and every place that names an app reads the same registry.
 */
const APP_NAMES: Record<string, string> = { all: 'All apps' };
const OS_NAMES: Record<string, string> = { ios: 'iOS', ipados: 'iPadOS', android: 'Android', windows: 'Windows', macos: 'macOS', chromeos: 'ChromeOS', linux: 'Linux' };
/** Memory as a number, so "at least 8 GB" can compare. "8 GB or more" is 8. */
const gb = (m: string) => Number((/(\d+)/.exec(m) ?? [])[1] ?? 0);

// ---- The query ------------------------------------------------------------

type FieldKey = 'app' | 'deviceType' | 'os' | 'memory' | 'storage' | 'browser' | 'experience' | 'time' | 'status';
type Condition = { field: FieldKey; value: string };

const FIELDS: { key: FieldKey; label: string; verb: string; values: { value: string; label: string }[] }[] = [
  /* Filled from the apps in the response when the screen loads. */
  { key: 'app', label: 'Application', verb: 'signed up for', values: [] },
  { key: 'deviceType', label: 'Device type', verb: 'has a', values: [{ value: 'phone', label: 'Phone' }, { value: 'tablet', label: 'Tablet' }, { value: 'computer', label: 'Computer' }] },
  { key: 'os', label: 'Operating system', verb: 'has a device on', values: Object.entries(OS_NAMES).map(([value, label]) => ({ value, label })) },
  { key: 'memory', label: 'Memory', verb: 'has a device with at least', values: ['4', '6', '8', '16'].map((v) => ({ value: v, label: `${v} GB` })) },
  { key: 'storage', label: 'Storage', verb: 'has a device with at least', values: ['64', '128', '256', '512'].map((v) => ({ value: v, label: `${v} GB` })) },
  { key: 'browser', label: 'Browser', verb: 'uses', values: ['Chrome', 'Safari', 'Microsoft Edge', 'Firefox', 'Samsung Internet'].map((v) => ({ value: v, label: v })) },
  { key: 'experience', label: 'Testing experience', verb: 'has tested', values: Object.entries(EXPERIENCE).map(([value, label]) => ({ value, label })) },
  { key: 'time', label: 'Time per week', verb: 'can give', values: [{ value: '15', label: 'At least 15 minutes' }, { value: '60', label: 'At least an hour' }, { value: '180', label: 'A few hours' }] },
  { key: 'status', label: 'Card', verb: 'is', values: [{ value: 'sent', label: 'Sent' }, { value: 'draft', label: 'Not sent yet' }] },
];
const fieldOf = (k: FieldKey) => FIELDS.find((f) => f.key === k)!;
const MINUTES: Record<string, number> = { '15m': 15, '1h': 60, more: 180 };

function matches(t: Tester, c: Condition): boolean {
  switch (c.field) {
    case 'app': return t.apps.includes(c.value) || t.apps.includes('all');
    case 'deviceType': return t.devices.some((d) => d.type === c.value);
    case 'os': return t.devices.some((d) => d.os === c.value);
    case 'memory': return t.devices.some((d) => gb(d.memory) >= Number(c.value));
    case 'storage': return t.devices.some((d) => gb(d.storage) * (/TB/.test(d.storage) ? 1024 : 1) >= Number(c.value));
    case 'browser': return t.devices.some((d) => d.browser === c.value);
    case 'experience': return t.experience === c.value;
    case 'time': return (MINUTES[t.time] ?? 0) >= Number(c.value);
    case 'status': return c.value === 'sent' ? t.sent : !t.sent;
  }
}
function conditionText(c: Condition) {
  const f = fieldOf(c.field);
  return `${f.label}: ${f.values.find((v) => v.value === c.value)?.label ?? c.value}`;
}

// ---- The screen -------------------------------------------------------------

export function Testers() {
  const { state } = useAsync(async () => {
    const d = await adminApi.testers();
    for (const a of d.apps) APP_NAMES[a.slug] = a.name;
    const appField = fieldOf('app');
    appField.values = d.apps
      .filter((a) => d.testers.some((t) => t.apps.includes(a.slug)) || ['in-dev', 'alpha', 'beta'].includes(a.status))
      .map((a) => ({ value: a.slug, label: a.name }));
    return { testers: d.testers.map(fromRow), firstApp: appField.values[0]?.value };
  }, []);
  return (
    <AsyncBoundary state={state}>
      {(d) => <TesterList testers={d.testers} firstApp={d.firstApp} />}
    </AsyncBoundary>
  );
}

function TesterList({ testers, firstApp }: { testers: Tester[]; firstApp?: string }) {
  // Opens on the first app taking testers, the question usually being asked.
  const [conditions, setConditions] = useState<Condition[]>(firstApp ? [{ field: 'app', value: firstApp }] : []);
  const [join, setJoin] = useState<'all' | 'any'>('all');
  const [editing, setEditing] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<null | 'add-people' | 'new-group' | 'add-to-group' | { card: Tester }>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const fromQuery = useMemo(
    () =>
      new Set(
        testers
          .filter((t) =>
            conditions.length === 0 ? true : join === 'all' ? conditions.every((c) => matches(t, c)) : conditions.some((c) => matches(t, c)),
          )
          .map((t) => t.id),
      ),
    [testers, conditions, join],
  );
  const inView = testers.filter((t) => (fromQuery.has(t.id) || added.has(t.id)) && !removed.has(t.id));
  const removedRows = testers.filter((t) => removed.has(t.id) && (fromQuery.has(t.id) || added.has(t.id)));
  const byHand = inView.filter((t) => added.has(t.id) && !fromQuery.has(t.id)).length;
  const selected = inView.filter((t) => checked.has(t.id));
  const allChecked = inView.length > 0 && selected.length === inView.length;

  const remove = (ids: string[]) => {
    setRemoved((r) => new Set([...r, ...ids]));
    setChecked((c) => new Set([...c].filter((x) => !ids.includes(x))));
  };
  const putBack = (id: string) => setRemoved((r) => new Set([...r].filter((x) => x !== id)));
  const resetHand = () => {
    setAdded(new Set());
    setRemoved(new Set());
  };

  return (
    <Screen
      filters={
        selected.length > 0 ? (
          <>
            <span style={{ fontSize: '.85rem', fontWeight: 600 }}>
              {count(selected.length)} tester{selected.length === 1 ? '' : 's'} selected
            </span>
            {!allChecked ? (
              <button type="button" style={linkBtn} onClick={() => setChecked(new Set(inView.map((t) => t.id)))}>
                Select all {count(inView.length)}
              </button>
            ) : null}
            <button type="button" style={linkBtn} onClick={() => setChecked(new Set())}>
              Clear
            </button>
            <Button onClick={() => remove(selected.map((t) => t.id))}>Remove from view</Button>
          </>
        ) : (
          <span style={{ fontSize: '.85rem', color: C.muted }}>
            {count(testers.length)} tester cards, {count(testers.filter((t) => t.sent).length)} sent
          </span>
        )
      }
      actions={
        <>
          <Button onClick={() => setEditing((e) => !e)}>{editing ? 'Close query' : 'Query'}</Button>
          <Button onClick={() => setModal('add-people')}>+ Add testers</Button>
          <Menu
            label="Put the selection in a security group"
            trigger={
              <span style={{ ...groupTrigger, opacity: selected.length ? 1 : 0.5 }}>Add to security group ▾</span>
            }
          >
            {(close) =>
              selected.length === 0 ? (
                <div style={{ padding: '.5rem .7rem', fontSize: '.82rem', color: C.muted, maxWidth: 220 }}>
                  Select testers first, or Select all.
                </div>
              ) : (
                <>
                  <MenuItem onClick={() => { close(); setModal('new-group'); }}>New security group…</MenuItem>
                  <MenuItem onClick={() => { close(); setModal('add-to-group'); }}>Existing security group…</MenuItem>
                </>
              )
            }
          </Menu>
        </>
      }
    >
      {editing ? (
        <QueryEditor
          conditions={conditions}
          join={join}
          onApply={(c, j) => {
            setConditions(c);
            setJoin(j);
            setChecked(new Set());
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      <div style={strip} aria-live="polite">
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.4rem' }}>
          <span style={{ fontWeight: 650, color: C.text }}>
            {conditions.length ? (join === 'all' ? 'Matching all of' : 'Matching any of') : 'Every tester'}
          </span>
          {conditions.map((c, i) => (
            <Badge key={i}>{conditionText(c)}</Badge>
          ))}
          <button type="button" style={linkBtn} onClick={() => setEditing(true)}>
            Edit
          </button>
        </span>
        <span style={{ color: C.textMid }}>
          {count(fromQuery.size)} from the query
          {byHand ? `, ${count(byHand)} added by hand` : ''}
          {removedRows.length ? `, ${count(removedRows.length)} removed by hand` : ''}
          {byHand || removedRows.length ? (
            <>
              {' · '}
              <button type="button" style={linkBtn} onClick={resetHand}>
                Back to the query
              </button>
            </>
          ) : null}
        </span>
      </div>

      <Panel title={`Testers in this view (${count(inView.length)})`}>
        {inView.length === 0 ? (
          <EmptyState title="Nobody matches" suggestion="Loosen the query, switch it to match any condition, or add testers by hand." />
        ) : (
          <Table
            actionsLast
            columns={[
              <input
                key="all"
                type="checkbox"
                checked={allChecked}
                aria-label="Select every tester in this view"
                onChange={() => setChecked(allChecked ? new Set() : new Set(inView.map((t) => t.id)))}
              />,
              'Tester',
              'Apps',
              'Devices',
              'Most memory',
              'Card',
              '',
            ]}
          >
            {inView.map((t) => {
              const isChecked = checked.has(t.id);
              return (
                <tr key={t.id} className="rw-rowhover" style={{ background: isChecked ? C.blueTint : undefined }}>
                  <Td>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      aria-label={`Select ${t.email}`}
                      onChange={() =>
                        setChecked((cur) => {
                          const next = new Set(cur);
                          if (next.has(t.id)) next.delete(t.id);
                          else next.add(t.id);
                          return next;
                        })
                      }
                    />
                  </Td>
                  <Td>
                    <button type="button" style={rowLink} onClick={() => setModal({ card: t })}>
                      {t.email}
                    </button>
                    <div style={sub}>
                      {t.name}
                      {added.has(t.id) && !fromQuery.has(t.id) ? <> · <span style={{ color: C.blueText, fontWeight: 600 }}>added by hand</span></> : null}
                    </div>
                  </Td>
                  <Td>
                    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
                      {t.apps.map((a) => <Badge key={a}>{APP_NAMES[a] ?? a}</Badge>)}
                    </span>
                  </Td>
                  <Td>
                    {t.devices.map((d, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.82rem', lineHeight: 1.5 }}>
                        <DeviceIcon type={d.type} />
                        <span>{d.model || OS_NAMES[d.os]}</span>
                        <span style={{ color: C.muted }}>{OS_NAMES[d.os]} {d.version}</span>
                      </div>
                    ))}
                  </Td>
                  <Td>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t.devices.some((d) => gb(d.memory)) ? `${Math.max(...t.devices.map((d) => gb(d.memory)))} GB` : '\u2013'}
                    </span>
                  </Td>
                  <Td>
                    {t.sent ? (
                      <span style={{ fontSize: '.82rem' }}>
                        Sent <When iso={t.submittedAt} />
                      </span>
                    ) : (
                      <Badge tone="warn">Not sent yet</Badge>
                    )}
                  </Td>
                  <Td>
                    <Menu label={`Actions for ${t.email}`}>
                      {(close) => (
                        <>
                          <MenuItem onClick={() => { close(); setModal({ card: t }); }}>View tester card</MenuItem>
                          <MenuItem href={`#/users/${encodeURIComponent(t.id)}`} onClick={close}>View account</MenuItem>
                          <MenuItem onClick={() => { close(); remove([t.id]); }}>Remove from this view</MenuItem>
                        </>
                      )}
                    </Menu>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Panel>

      {removedRows.length ? (
        <div style={{ marginTop: '1rem' }}>
          <button type="button" style={linkBtn} onClick={() => setShowRemoved((v) => !v)} aria-expanded={showRemoved}>
            {showRemoved ? '▾' : '▸'} Removed from this view ({count(removedRows.length)})
          </button>
          {showRemoved ? (
            <div style={{ marginTop: '.6rem' }}>
              <Panel title="Removed by hand">
                <Table actionsLast columns={['Tester', 'Apps', '']}>
                  {removedRows.map((t) => (
                    <tr key={t.id} className="rw-rowhover">
                      <Td>{t.email}<div style={sub}>{t.name}</div></Td>
                      <Td>{t.apps.map((a) => APP_NAMES[a] ?? a).join(', ')}</Td>
                      <Td><Button onClick={() => putBack(t.id)}>Put back</Button></Td>
                    </tr>
                  ))}
                </Table>
              </Panel>
            </div>
          ) : null}
        </div>
      ) : null}

      {modal === 'add-people' ? (
        <AddPeople
          candidates={testers.filter((t) => !inView.some((v) => v.id === t.id))}
          onClose={() => setModal(null)}
          onAdd={(ids) => {
            setAdded((a) => new Set([...a, ...ids]));
            setRemoved((r) => new Set([...r].filter((x) => !ids.includes(x))));
            setModal(null);
          }}
        />
      ) : null}
      {modal === 'new-group' || modal === 'add-to-group' ? (
        <ToGroup
          mode={modal === 'new-group' ? 'new' : 'existing'}
          testers={selected}
          conditions={conditions}
          onClose={() => setModal(null)}
          // Not a reload: the cards did not change, and reloading would drop the
          // hand-made list the person just built.
          onDone={() => setChecked(new Set())}
        />
      ) : null}
      {modal && typeof modal === 'object' ? <CardModal tester={modal.card} onClose={() => setModal(null)} /> : null}
    </Screen>
  );
}

// ---- Query editor -----------------------------------------------------------

function QueryEditor({
  conditions: initial,
  join: initialJoin,
  onApply,
  onCancel,
}: {
  conditions: Condition[];
  join: 'all' | 'any';
  onApply: (c: Condition[], j: 'all' | 'any') => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState<Condition[]>(initial);
  const [join, setJoin] = useState(initialJoin);
  const set = (i: number, c: Condition) => setRows(rows.map((r, j) => (j === i ? c : r)));
  return (
    <div style={{ marginBottom: '1rem' }}>
      <Panel title="Query">
        <PanelBody>
          <div style={{ display: 'grid', gap: '.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', fontSize: '.85rem' }}>
              <span>Show testers who match</span>
              <span style={{ width: 90 }}>
                <Select label="Match all or any" value={join} onChange={setJoin} size="sm" options={[{ value: 'all', label: 'all' }, { value: 'any', label: 'any' }]} />
              </span>
              <span>of these:</span>
            </div>
            {rows.map((r, i) => {
              const f = fieldOf(r.field);
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '200px 180px 220px auto', justifyContent: 'start', gap: '.6rem', alignItems: 'center' }}>
                  <Select
                    block
                    label="Field"
                    value={r.field}
                    onChange={(k) => set(i, { field: k, value: fieldOf(k).values[0]?.value ?? "" })}
                    options={FIELDS.map((x) => ({ value: x.key, label: x.label }))}
                  />
                  <span style={{ fontSize: '.82rem', color: C.muted, whiteSpace: 'nowrap' }}>{f.verb}</span>
                  <Select block label="Value" value={r.value} onChange={(v) => set(i, { ...r, value: v })} options={f.values} />
                  <button type="button" style={{ ...linkBtn, color: C.dangerText }} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove this condition">
                    Remove
                  </button>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center', marginTop: '.3rem' }}>
              <Button onClick={() => setRows([...rows, { field: 'os', value: 'android' }])}>+ Add condition</Button>
              <span style={{ flex: 1 }} />
              <Button onClick={onCancel}>Cancel</Button>
              <PrimaryButton onClick={() => onApply(rows, join)}>Run query</PrimaryButton>
            </div>
            <span style={{ fontSize: '.78rem', color: C.muted }}>
              Running a query keeps anybody you added or removed by hand. Use Back to the query to drop those.
            </span>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

// ---- Modals -------------------------------------------------------------------

function AddPeople({ candidates, onClose, onAdd }: { candidates: Tester[]; onClose: () => void; onAdd: (ids: string[]) => void }) {
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const shown = candidates.filter((t) => `${t.name} ${t.email}`.toLowerCase().includes(term.toLowerCase()));
  return (
    <Modal
      title="Add testers to this view"
      subtitle="Testers the query did not find. Adding them here changes this list only, not their card."
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <PrimaryButton disabled={picked.size === 0} onClick={() => onAdd([...picked])}>
            Add {picked.size ? count(picked.size) : ''} to the view
          </PrimaryButton>
        </ModalActions>
      }
    >
      <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search name or email" style={inputStyle} aria-label="Search testers" />
      <div style={{ display: 'grid', maxHeight: 320, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
        {shown.length === 0 ? <div style={{ padding: '.8rem', color: C.muted, fontSize: '.85rem' }}>Everybody is already in the view.</div> : null}
        {shown.map((t) => (
          <label key={t.id} style={{ display: 'flex', gap: '.6rem', alignItems: 'center', padding: '.55rem .75rem', borderTop: `1px solid ${C.border}`, fontSize: '.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={picked.has(t.id)}
              onChange={() => setPicked((p) => { const n = new Set(p); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; })}
            />
            <span style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{t.email}</span>
              <span style={{ display: 'block', color: C.muted, fontSize: '.78rem' }}>
                {t.apps.map((a) => APP_NAMES[a] ?? a).join(', ')} · {t.devices.map((d) => d.model || OS_NAMES[d.os]).join(', ')}
              </span>
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

function ToGroup({
  mode,
  testers: picked,
  conditions,
  onClose,
  onDone,
}: {
  mode: 'new' | 'existing';
  testers: Tester[];
  conditions: Condition[];
  onClose: () => void;
  onDone: () => void;
}) {
  // Held at open: the page clears its selection once they are added, and this
  // dialog still has to say who went in.
  const [testers] = useState(picked);
  const groups = useAsync(() => adminApi.securityGroups(), []);
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const suggested = `${conditions.filter((c) => c.field === 'app').map((c) => APP_NAMES[c.value] ?? c.value).join(' and ') || 'App'} testers, ${today}`;
  const [name, setName] = useState(suggested);
  const [description, setDescription] = useState(conditions.length ? `Picked on the Testers screen: ${conditions.map(conditionText).join('; ')}.` : '');
  const [slug, setSlug] = useState('');
  const groupName = groups.state.status === 'ready' ? groups.state.data.groups.find((g) => g.slug === slug)?.name ?? slug : slug;
  const action = useAction();
  const [done, setDone] = useState<string | null>(null);
  const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const run = () =>
    void action
      .run(async () => {
        let target = slug;
        if (mode === 'new') {
          target = autoSlug;
          await adminApi.createSecurityGroup({ slug: target, name, description });
        }
        // Not the group's own members endpoint: this one also marks the group
        // as holding testers, so somebody deleting their card leaves it.
        await adminApi.addTestersToGroup(target, testers.map((t) => t.id));
        setDone(target);
        return `Added ${count(testers.length)} tester${testers.length === 1 ? '' : 's'} to ${mode === 'new' ? name : groupName}.`;
      })
      .then(onDone);

  return (
    <Modal
      title={mode === 'new' ? 'New security group from these testers' : 'Add these testers to a security group'}
      subtitle={`${count(testers.length)} tester${testers.length === 1 ? '' : 's'} selected. Their tester cards are not changed.`}
      onClose={onClose}
      footer={
        <ModalActions>
          {done ? (
            <>
              <Button href={`#/security-groups/${encodeURIComponent(done)}`}>Open the group</Button>
              <PrimaryButton onClick={onClose}>Done</PrimaryButton>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <PrimaryButton disabled={action.running || (mode === 'new' ? !name.trim() : !slug)} onClick={run}>
                {mode === 'new' ? `Create group and add ${count(testers.length)}` : `Add ${count(testers.length)}`}
              </PrimaryButton>
            </>
          )}
        </ModalActions>
      }
    >
      {mode === 'new' ? (
        <>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Slug" hint="Permanent once created.">
            <input value={autoSlug} readOnly disabled style={{ ...inputStyle, background: C.page }} />
          </Field>
          <Field label="Description" hint="Filled in from the query so the group says how it was picked. Edit freely.">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
          <span style={{ fontSize: '.8rem', color: C.muted }}>
            The group starts with no applications or roles. Grant it the app on the group's page, the same as any other group.
          </span>
        </>
      ) : (
        <AsyncBoundary state={groups.state}>
          {(d) => (
            <Field label="Security group" hint="People already in the group are skipped.">
              <Select
                block
                label="Security group"
                value={slug}
                onChange={setSlug}
                options={[{ value: '', label: 'Choose a group' }, ...d.groups.filter((g: SecurityGroup) => !g.protected).map((g: SecurityGroup) => ({ value: g.slug, label: `${g.name} (${count(g.members)})` }))]}
              />
            </Field>
          )}
        </AsyncBoundary>
      )}
      <Who testers={testers} />
      <OutcomeNote outcome={action.outcome} />
    </Modal>
  );
}

function Who({ testers }: { testers: Tester[] }) {
  const shown = testers.slice(0, 6);
  return (
    <div style={{ fontSize: '.82rem', color: C.textMid, lineHeight: 1.5 }}>
      {shown.map((t) => t.name).join(', ')}
      {testers.length > shown.length ? ` and ${count(testers.length - shown.length)} more` : ''}
    </div>
  );
}

function CardModal({ tester: t, onClose }: { tester: Tester; onClose: () => void }) {
  return (
    <Modal
      title={t.name}
      subtitle={<>{t.email} · {t.sent ? <>card sent <When iso={t.submittedAt} />, updated <When iso={t.updatedAt} /></> : 'card not sent yet'}</>}
      width={620}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button href={`#/users/${encodeURIComponent(t.id)}`}>View account</Button>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </ModalActions>
      }
    >
      <Facts
        rows={[
          ['Apps', t.apps.map((a) => APP_NAMES[a] ?? a).join(', ')],
          ['Tested before', EXPERIENCE[t.experience] ?? 'Not answered'],
          ['Feedback', FEEDBACK[t.feedback] ?? 'Not answered'],
          ['Time', TIME[t.time] ?? 'Not answered'],
          ['Invites go to', t.inviteEmail || 'Not given'],
          ['Accessibility', t.accessibility.map((a) => ACCESSIBILITY[a] ?? a).join(', ') || 'None given'],
        ]}
      />
      <div style={{ display: 'grid', gap: '.5rem' }}>
        <span style={{ fontSize: '.85rem', fontWeight: 650, color: C.textMid }}>Devices</span>
        {t.devices.map((d, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '.6rem', alignItems: 'start', border: `1px solid ${C.border}`, borderRadius: 8, padding: '.6rem .75rem' }}>
            <DeviceIcon type={d.type} size={20} />
            <span style={{ fontSize: '.85rem' }}>
              <b>{d.model || OS_NAMES[d.os]}</b>
              <span style={{ display: 'block', color: C.textMid }}>
                {OS_NAMES[d.os]} {d.version} · {d.storage} · {d.memory} memory · {d.browser}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '.45rem .8rem', margin: 0, fontSize: '.85rem' }}>
      {rows.map(([k, v]) => (
        <span key={k} style={{ display: 'contents' }}>
          <dt style={{ color: C.muted }}>{k}</dt>
          <dd style={{ margin: 0 }}>{v}</dd>
        </span>
      ))}
    </dl>
  );
}

function DeviceIcon({ type, size = 16 }: { type: TesterDevice['type']; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: C.brand, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-label': type, role: 'img' };
  if (type === 'phone') return <svg {...common}><rect x="6.5" y="2" width="11" height="20" rx="2.5" /><path d="M11 18.5h2" /></svg>;
  if (type === 'tablet') return <svg {...common}><rect x="3.5" y="2.5" width="17" height="19" rx="2.5" /><path d="M11 18.5h2" /></svg>;
  return <svg {...common}><rect x="3" y="4" width="18" height="12" rx="1.8" /><path d="M8 20h8M12 16v4" /></svg>;
}

const linkBtn = { background: 'none', border: 'none', padding: 0, color: C.brand, fontWeight: 600, fontSize: '.85rem', cursor: 'pointer' };
const rowLink = { ...linkBtn, textAlign: 'left' as const };
const sub = { fontSize: '.78rem', color: C.muted, marginTop: 2 };
const strip = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap' as const,
  gap: '.5rem 1rem',
  fontSize: '.85rem',
  padding: '.65rem .9rem',
  marginBottom: '1rem',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  background: C.surface,
};
const groupTrigger = {
  display: 'inline-block',
  padding: '.42rem .8rem',
  fontSize: '.85rem',
  fontWeight: 640,
  borderRadius: 6,
  border: `1px solid ${C.brand}`,
  background: C.brand,
  color: C.surface,
};
